import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { Config } from 'src/modules/app-template/models/config.model';
import { GuiUtils } from 'src/modules/utils/misc/gui-utils';
import { Objects } from "src/modules/utils/shared/objects";
import { Strings } from "src/modules/utils/shared/strings";
import { Utils } from 'src/modules/utils/shared/utils';
import { HttpConnectorError } from "../models/http-connector-error.model";
import { RestEndpoint } from '../models/rest-endpoint.model';
import { UserLoginDto } from '../netgen/UserLoginDto';
import { AuthLoginResult } from '../shared/AuthLoginResult';
import { LoginUser } from '../shared/LoginUser';

@Injectable({providedIn: "root"})
export class AuthenticationService {

    router = inject(Router);

    allowAnonymous = false;
    userDetails: LoginUser = null;

    onUserChanged = new Subject<void>();

    private accessToken: string = null;

    constructor() {
        GuiUtils.checkSingleton(this);
        this.allowAnonymous = Config.get().allowAnonymous;
    }

    getToken(): string {
        if (this.accessToken == null) {
            let accessToken = localStorage.getItem('accessToken');
            if (!Utils.isNoe(accessToken)) {
                this.accessToken = accessToken;
            }
        }
        return this.accessToken;
    }

    getUserName(): string {
        return this.userDetails?.userName;
    }

    async clearLogin(returnUrl = true): Promise<void> {
        if (this.allowAnonymous) {
            return;
        }

        this.setToken(null);
        this.userDetails = null;
        let url = Strings.removePrefix(window.location.href, window.location.origin);
        if (url.startsWith("/base/")) {
            return;
        }
        if (url.length <= 1) {
            returnUrl = false;
        }
        await this.router.navigateByUrl("/base/login" + (returnUrl ? "?returnUrl=" + Strings.removePrefix(url, "/base/login?returnUrl=") : ""));
    }

    isLoggedIn(): boolean {
        return this.allowAnonymous || this.userDetails != null;
    }

    setToken(accessToken: string): void {
        this.accessToken = accessToken;
        localStorage.setItem('accessToken', accessToken || "");
    }

    async login(data: UserLoginDto): Promise<LoginUser> {
        try {
            let res = await (Config.get().backendV2 ? RestEndpoint.main() : RestEndpoint.main().contentTypeForm()).body(data).post().run("api/app/auth/login").get(LoginUser);
            if (!Utils.isNoe(res.accessToken)) {
                this.setToken(res.accessToken);
                await this.updateUserDetails();
                this.onUserChanged.next();
            }
            return res;
        }
        catch (error) {
            console.log("Fehler beim Login: " + Objects.toJson(error));
            return Objects.fromPlain(LoginUser, {
                loginResult: AuthLoginResult.unspecifiedError
            });
        }
    }

    async logout(): Promise<void> {
        await RestEndpoint.main().run("api/app/auth/logout").getText();
        await this.clearLogin(false);
    }  

    async changePassword(oldPassword: string, newPassword: string): Promise<boolean> {
        return RestEndpoint.main().post().body({oldPassword, newPassword}).run("api/app/auth/changePassword").getBool();
    }

    async updateUserDetails(): Promise<void> {
        if (this.allowAnonymous) {
            this.userDetails = Objects.fromPlain(LoginUser, {
                userName: "Anonymous",
                accessToken: ""
            });
        }
        else {
            try {
                let s = await RestEndpoint.main().run("api/app/auth/getuserdetails").getText();
                if (Utils.isNoe(s)) {
                    await this.clearLogin();
                }
                else {
                    this.userDetails = Objects.fromPlainUnsafe(LoginUser, Objects.fromJson(s));
                    if (Strings.is(this.userDetails)) {
                        throw new Error("userDetails ist vom Typ string: " + s);
                    }
                }
            }
            catch (error) {
                //Verbindungsproblem - Nicht den Login löschen
                if (error instanceof HttpConnectorError && error.response?.status == 0) {
                    return;
                }
                await this.clearLogin();
            }
        }
    }
}